import { type ReactNode } from 'react'
import { FormLayoutSections, type FormLayoutField, type FormLayoutSection } from '../form/FormLayoutSections'
import s from './PeekPanel.module.css'

/**
 * The PEEK preview — one related row expanded in place into a read-only view of
 * that record's own canonical form (#2281).
 *
 * ── it composes the record's renderer, it does not grow a second one ───────
 * The sections arrive as `FormLayoutSection`s, which is what the canonical
 * record page renders and what the portal form renders. So a field here is laid
 * out, labelled, formatted and CLASSIFIED exactly as it is on the full record,
 * because it is the same component reading the same resolved document. A
 * "mini-form" with its own field list would be a second schema, which #2281
 * rules out in as many words — and would be the surface where a hidden field
 * quietly reappears.
 *
 * ── it is DATA-FREE, and that is the security property ─────────────────────
 * It fetches nothing, resolves nothing and decides no authority. The host hands
 * over sections it has already resolved and already had authorized for this
 * caller; a `when` that evaluated false, a field the caller may not read and a
 * refused document never become props. There is no door here to widen.
 *
 * ── the states are explicit, because a blank preview is a lie ──────────────
 * Refused, empty, still-loading and error are four different sentences. A
 * preview that failed and rendered nothing would read as "this record has
 * nothing in it", which is the reassuring blank #2281 forbids.
 */
export interface PeekPanelProps<F extends FormLayoutField> {
  /** The record being previewed, for the accessible name and the open action. */
  title: string
  /**
   * Sections of the target's canonical form, resolved and ALREADY AUTHORIZED.
   * Empty means the form resolved and showed nothing, which is its own state.
   */
  sections: readonly FormLayoutSection<F>[]
  renderField: (field: F, section: FormLayoutSection<F>) => ReactNode
  /** The canonical destination. Absent ⇒ the record cannot be opened from here. */
  onOpenRecord?: () => void
  /** A refusal or failure, already worded by the host. */
  problem?: ReactNode
  busy?: boolean
  /** Binds the preview to the row that disclosed it. */
  id?: string
  labelledBy?: string
  testId?: string
}

export function PeekPanel<F extends FormLayoutField>({
  title, sections, renderField, onOpenRecord, problem, busy, id, labelledBy,
  testId = 'peek-panel',
}: PeekPanelProps<F>) {
  const body = problem
    ? <p className={s.problem} data-testid={`${testId}-problem`}>{problem}</p>
    : busy
      ? <p className={s.muted} data-testid={`${testId}-busy`}>Looking…</p>
      : sections.length === 0
        ? (
          /* The form RESOLVED and had nothing to show. Said out loud, because
             "no sections" and "we could not read it" lead to different actions
             and the difference is invisible if both render empty. */
          <p className={s.muted} data-testid={`${testId}-empty`}>
            This record’s form shows no details here.
          </p>
        )
        : (
          <FormLayoutSections<F>
            sections={sections as FormLayoutSection<F>[]}
            renderField={renderField}
            data-testid={`${testId}-sections`}
          />
        )

  return (
    /* A REGION, named by the row that opened it. A preview that announced
       nothing would leave a screen-reader user with a disclosure that expanded
       into unlabelled content. */
    <section className={s.root} id={id} aria-labelledby={labelledBy}
      data-testid={testId}
      {...(labelledBy ? {} : { 'aria-label': `Preview of ${title}` })}>
      {body}

      {/* The whole preview is NOT a navigation target. #2281: fields stay
          selectable and readable, and ONE explicit action opens the record —
          otherwise selecting a value to copy it navigates away instead. */}
      {onOpenRecord && (
        <div className={s.actions}>
          <button type="button" className={s.open} data-testid={`${testId}-open`}
            onClick={onOpenRecord}>
            Open full record
          </button>
        </div>
      )}
    </section>
  )
}
